<div class="min-h-screen bg-gray-50 dark:bg-slate-900 p-4 md:p-6 space-y-6">
  <!-- HEADER — toujours visible, données pré-remplies depuis le cache -->
  <header class="flex items-center justify-between">
    <div>
      <h1 class="text-2xl font-semibold text-gray-900 dark:text-white">
        📊 Tableau de bord - {{ businessName || "Mon commerce" }}
      </h1>
      <div class="flex flex-wrap items-center gap-2 mt-2">
        <span
          class="inline-block px-3 py-1 text-xs font-semibold text-green-700 dark:text-green-300 bg-green-100 dark:bg-green-900/30 rounded-full"
        >
          🚚 Commerce itinérant
        </span>
        @if (activityType && activityTypeLabels[activityType]) {
          <span
            class="inline-block px-3 py-1 text-xs font-semibold text-purple-700 dark:text-purple-300 bg-purple-100 dark:bg-purple-900/30 rounded-full"
          >
            {{ activityTypeLabels[activityType] }}
          </span>
        }
        @if (userServices.length > 0) {
          <span
            class="inline-block px-3 py-1 text-xs font-semibold text-blue-700 dark:text-blue-300 bg-blue-100 dark:bg-blue-900/30 rounded-full"
          >
            Service :
            @for (service of userServices; track $index; let last = $last) {
              <span>
                {{ serviceLabels[service] }}
                @if (!last) {
                  <span>, </span>
                }
              </span>
            }
          </span>
        }
        @if (affluence && affluenceLabels[affluence]) {
          <span
            [ngClass]="{
              'text-orange-700 dark:text-orange-300 bg-orange-100 dark:bg-orange-900/30':
                affluence === 'low',
              'text-blue-700 dark:text-blue-300 bg-blue-100 dark:bg-blue-900/30':
                affluence === 'medium',
              'text-red-700 dark:text-red-300 bg-red-100 dark:bg-red-900/30':
                affluence === 'high',
            }"
            class="inline-block px-3 py-1 text-xs font-semibold rounded-full"
          >
            {{ affluenceLabels[affluence].icon }} Affluence :
            {{ affluenceLabels[affluence].label }}
          </span>
        }
      </div>
      @if (userAddress) {
        <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">
          📍 {{ userAddress }}
        </p>
      }
    </div>
    <!-- Menu card button -->
    <button
      (click)="showMenuCard = true"
      class="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 hover:bg-amber-200 dark:hover:bg-amber-900/50 transition-colors"
      title="Voir mon menu"
    >
      📋 Carte du menu
    </button>
  </header>

  <!-- Menu card modal -->
  @if (showMenuCard) {
    <app-menu-card
      merchantType="itinerant"
      [businessName]="businessName"
      (closed)="showMenuCard = false"
    />
  }

  <!-- KPIs — skeleton inline si loading -->
  <section class="flex flex-wrap gap-2">
    <!-- Événements suivis ce mois -->
    <div
      class="rounded-lg shadow-sm dark:shadow-slate-900/30 p-2 bg-white dark:bg-slate-800 hover:shadow-md dark:shadow-slate-900/50 transition-shadow flex items-center gap-2 flex-shrink-0 flex-1 min-w-[130px]"
    >
      <span class="text-base shrink-0">📊</span>
      <div class="flex-1 min-w-0">
        <p
          class="text-[10px] text-gray-500 dark:text-gray-400 leading-tight truncate"
        >
          Suivis ce mois
        </p>
        <p class="text-sm font-bold text-purple-700 dark:text-purple-300">
          {{ kpis ? kpis.eventsTrackedThisMonth : 0 }}
          <span class="text-[10px] font-normal text-gray-400"
            >/ {{ kpis ? kpis.eventsThisMonth : 0 }} événements</span
          >
        </p>
      </div>
      @if (!isEmptyState && kpis && kpis.eventsThisMonthDiff !== 0) {
        <span
          class="text-[10px] px-1 py-0.5 rounded-full shrink-0"
          [ngClass]="
            kpis.eventsThisMonthDiff > 0
              ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
              : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
          "
        >
          {{ kpis.eventsThisMonthDiff > 0 ? "↗+" : "↘"
          }}{{ kpis.eventsThisMonthDiff }}
        </span>
      }
      @if (isEmptyState) {
        <span
          class="text-[10px] px-1 py-0.5 rounded-full shrink-0 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300"
          >Démarrez !</span
        >
      }
    </div>
    <!-- Gain SaaS (delta bénéfice AVEC vs SANS outil) -->
    <div
      class="rounded-lg shadow-sm dark:shadow-slate-900/30 p-2 bg-green-50 dark:bg-green-900/20 hover:shadow-md dark:shadow-slate-900/50 transition-shadow flex items-center gap-2 flex-shrink-0 flex-1 min-w-[130px]"
      [class.opacity-60]="isEmptyState"
    >
      <span class="text-base shrink-0">💚</span>
      <div class="flex-1 min-w-0">
        <p
          class="text-[10px] text-gray-500 dark:text-gray-400 leading-tight truncate"
        >
          Gain SaaS vs sans outil
        </p>
        <p class="text-sm font-bold text-green-700 dark:text-green-300">
          {{
            kpis && kpis.totalSavings > 0 ? "+" + kpis.totalSavings + "€" : "0€"
          }}
        </p>
      </div>
      @if (!isEmptyState && kpis && kpis.totalSavingsTrend != null) {
        <span
          class="text-[10px] px-1 py-0.5 rounded-full shrink-0"
          [ngClass]="
            kpis.totalSavingsTrend > 0
              ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
              : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
          "
        >
          {{ kpis.totalSavingsTrend > 0 ? "↗+" : "↘"
          }}{{ kpis.totalSavingsTrend }}%
        </span>
      }
      @if (isEmptyState) {
        <span
          class="text-[10px] px-1 py-0.5 rounded-full shrink-0 bg-green-200 text-green-800 dark:text-green-300"
          >Potentiel</span
        >
      }
    </div>
    <!-- Bénéfice net moyen / événement -->
    <div
      class="rounded-lg shadow-sm dark:shadow-slate-900/30 p-2 bg-white dark:bg-slate-800 hover:shadow-md dark:shadow-slate-900/50 transition-shadow flex items-center gap-2 flex-shrink-0 flex-1 min-w-[140px]"
      [class.opacity-60]="isEmptyState"
    >
      <span class="text-base shrink-0">💰</span>
      <div class="flex-1 min-w-0">
        <p
          class="text-[10px] text-gray-500 dark:text-gray-400 leading-tight truncate"
        >
          Bénéfice net moy/évent
        </p>
        <p class="text-sm font-bold text-purple-700 dark:text-purple-300">
          {{
            kpis && kpis.avgNetProfitPerEvent != null
              ? kpis.avgNetProfitPerEvent + "€"
              : "–"
          }}
        </p>
      </div>
      @if (!isEmptyState && kpis && kpis.avgNetProfitPerEventTrend != null) {
        <span
          class="text-[10px] px-1 py-0.5 rounded-full shrink-0"
          [ngClass]="
            kpis.avgNetProfitPerEventTrend > 0
              ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
              : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
          "
        >
          {{ kpis.avgNetProfitPerEventTrend > 0 ? "↗+" : "↘"
          }}{{ kpis.avgNetProfitPerEventTrend }}%
        </span>
      }
      @if (isEmptyState) {
        <span
          class="text-[10px] px-1 py-0.5 rounded-full shrink-0 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300"
          >À venir</span
        >
      }
    </div>
    <!-- Précision IA -->
    <div
      class="rounded-lg shadow-sm dark:shadow-slate-900/30 p-2 bg-white dark:bg-slate-800 hover:shadow-md dark:shadow-slate-900/50 transition-shadow flex items-center gap-2 flex-shrink-0 flex-1 min-w-[110px]"
      [class.opacity-60]="isEmptyState"
    >
      <span class="text-base shrink-0">🎯</span>
      <div class="flex-1 min-w-0">
        <p
          class="text-[10px] text-gray-500 dark:text-gray-400 leading-tight truncate"
        >
          Précision IA
        </p>
        <p class="text-sm font-bold text-purple-700 dark:text-purple-300">
          {{ kpis && kpis.aiPrecision != null ? kpis.aiPrecision + "%" : "–" }}
        </p>
      </div>
      @if (!isEmptyState && kpis && kpis.aiPrecisionTrend != null) {
        <span
          class="text-[10px] px-1 py-0.5 rounded-full shrink-0"
          [ngClass]="
            kpis.aiPrecisionTrend > 0
              ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
              : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
          "
        >
          {{ kpis.aiPrecisionTrend > 0 ? "↗+" : "↘"
          }}{{ kpis.aiPrecisionTrend }}%
        </span>
      }
      @if (isEmptyState) {
        <span
          class="text-[10px] px-1 py-0.5 rounded-full shrink-0 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300"
          >En calibrage</span
        >
      }
    </div>
    <!-- Top produit -->
    <div
      class="rounded-lg shadow-sm dark:shadow-slate-900/30 p-2 bg-white dark:bg-slate-800 hover:shadow-md dark:shadow-slate-900/50 transition-shadow flex items-center gap-2 flex-shrink-0 flex-1 min-w-[120px]"
      [class.opacity-60]="isEmptyState"
    >
      <span class="text-base shrink-0">⭐</span>
      <div class="flex-1 min-w-0">
        <p
          class="text-[10px] text-gray-500 dark:text-gray-400 leading-tight truncate"
        >
          Top produit
        </p>
        <p
          class="text-sm font-bold text-purple-700 dark:text-purple-300 truncate"
        >
          {{ kpis && kpis.topProduct ? kpis.topProduct : "À découvrir" }}
        </p>
      </div>
      @if (!isEmptyState && kpis && kpis.topProductSales) {
        <span
          class="text-[10px] px-1 py-0.5 rounded-full shrink-0 bg-gray-100 dark:bg-slate-800 text-gray-600 dark:text-gray-400"
          >{{ kpis.topProductSales }} vendus</span
        >
      }
      @if (isEmptyState) {
        <span
          class="text-[10px] px-1 py-0.5 rounded-full shrink-0 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300"
          >Bientôt</span
        >
      }
    </div>
  </section>
  <!-- ═══ EN COURS : événement actif ═══ -->
  @if (!isEmptyState && eventsByStatus.active.length > 0) {
    @for (event of eventsByStatus.active; track event.id) {
      <section
        class="bg-gradient-to-br from-green-500 to-emerald-700 text-white rounded-xl shadow-lg dark:shadow-slate-900/50 p-5"
      >
        <div class="flex items-start justify-between gap-4">
          <div>
            <p
              class="text-xs font-medium text-green-100 flex items-center gap-1 mb-1"
            >
              <span class="animate-pulse">🟢</span> En cours maintenant
            </p>
            <h2 class="text-xl font-bold">{{ event.name }}</h2>
            <div
              class="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm text-green-100"
            >
              <span>{{ formatLocation(event.location) }}</span>
              @if (event.startTime) {
                <span
                  >⏰ {{ event.startTime
                  }}{{ event.endTime ? " – " + event.endTime : "" }}</span
                >
              }
            </div>
          </div>
          <div class="flex gap-2 shrink-0 mt-1">
            <button
              (click)="goToKiosk(event.id)"
              class="px-4 py-2 text-sm bg-white dark:bg-slate-800 text-green-700 dark:text-green-300 font-semibold rounded-lg hover:bg-green-50 dark:bg-green-900/20 transition"
            >
              🛒 Kiosk
            </button>
            <button
              (click)="viewEvent(event.id)"
              class="px-4 py-2 text-sm bg-green-600 border border-green-300 font-semibold rounded-lg hover:bg-green-500 transition"
            >
              📋 Détail
            </button>
          </div>
        </div>

        <!-- ─── Météo de l'événement en cours ─── -->
        @if (timelineEventWeather.get(event.id)?.available) {
          <div
            class="mt-4 pt-4 border-t border-green-400 dark:border-green-700/40"
          >
            <p
              class="text-xs font-semibold text-green-200 uppercase tracking-wide mb-3"
            >
              Météo lors de l'événement
            </p>
            <div class="flex flex-wrap items-center gap-4">
              <!-- Icône + températures -->
              <div class="flex items-center gap-2">
                <span class="text-4xl leading-none">{{
                  getActiveEventWeatherIcon(event.id)
                }}</span>
                <div>
                  <p class="text-sm font-semibold leading-tight">
                    {{ $any(timelineEventWeather.get(event.id)).condition }}
                  </p>
                  <p class="text-xs text-green-200">
                    {{
                      $any(timelineEventWeather.get(event.id)).temperatureMin
                        | number: "1.0-0"
                    }}–{{
                      $any(timelineEventWeather.get(event.id)).temperatureMax
                        | number: "1.0-0"
                    }}°C
                    @if (
                      $any(timelineEventWeather.get(event.id))
                        .precipitationSum > 0
                    ) {
                      · 💧
                      {{
                        $any(timelineEventWeather.get(event.id))
                          .precipitationSum | number: "1.0-1"
                      }}
                      mm
                    }
                  </p>
                </div>
              </div>
              <!-- Alerte -->
              @if (getActiveEventWeatherIsAlert(event.id)) {
                <div
                  class="flex items-center gap-1.5 px-3 py-1.5 bg-orange-500/30 border border-orange-400/40 rounded-lg text-xs font-medium text-orange-100"
                >
                  <span>⚠️</span>
                  <span>Météo défavorable — adapter les quantités</span>
                </div>
              }
            </div>
            <!-- Créneaux horaires -->
            @if (activeEventHourlyWeather.get(event.id)?.available) {
              <div class="mt-3 flex gap-2 overflow-x-auto pb-1">
                @for (
                  slot of $any(activeEventHourlyWeather.get(event.id)).slots;
                  track slot.hour
                ) {
                  <div
                    class="flex-shrink-0 flex flex-col items-center gap-0.5 px-2 py-2 bg-green-600/40 rounded-lg min-w-[52px]"
                  >
                    <span class="text-[10px] text-green-200 font-medium">{{
                      slot.hour
                    }}</span>
                    <span class="text-lg leading-none">{{
                      hourlySlotEmoji(slot.weatherCode)
                    }}</span>
                    <span class="text-sm font-bold"
                      >{{ slot.temperature }}°</span
                    >
                    @if (slot.precipitationProbability > 20) {
                      <span class="text-[10px] text-blue-200"
                        >{{ slot.precipitationProbability }}%</span
                      >
                    }
                  </div>
                }
              </div>
            } @else if (
              !activeEventHourlyWeather.has(event.id) &&
              hasActiveEventCoords(event)
            ) {
              <!-- skeleton créneaux -->
              <div class="mt-3 flex gap-2">
                @for (i of [1, 2, 3, 4]; track i) {
                  <div
                    class="w-[52px] h-16 rounded-lg bg-green-600/20 animate-pulse"
                  ></div>
                }
              </div>
            }
          </div>
        } @else if (
          hasActiveEventCoords(event) && !timelineEventWeather.has(event.id)
        ) {
          <!-- skeleton résumé -->
          <div
            class="mt-4 pt-4 border-t border-green-400 dark:border-green-700/40 flex items-center gap-3"
          >
            <div
              class="w-9 h-9 rounded-full bg-green-500/30 animate-pulse"
            ></div>
            <div class="space-y-1.5">
              <div class="w-32 h-3 rounded bg-green-500/30 animate-pulse"></div>
              <div class="w-20 h-2 rounded bg-green-500/20 animate-pulse"></div>
            </div>
          </div>
        }
      </section>
    }
  }
  <!-- CALENDRIER MULTI-PÉRIODE -->
  <section
    class="rounded-2xl bg-white dark:bg-slate-900 shadow-lg dark:shadow-inner border border-gray-200 dark:border-slate-800"
  >
    <!-- ─── Header ─── -->
    <div class="px-6 pt-5 pb-0">
      <div
        class="flex flex-col sm:flex-row sm:items-start justify-between gap-3"
      >
        <div>
          <h2 class="text-xl font-semibold text-gray-900 dark:text-white">
            📅 Calendrier
          </h2>
          <p
            class="text-sm text-gray-500 dark:text-slate-400 mt-0.5 capitalize"
          >
            {{ getCalendarRangeLabel() }}
          </p>
        </div>
      </div>

      <!-- Tabs de vue + navigation -->
      <div
        class="flex items-center justify-between mt-4 border-b border-gray-200 dark:border-slate-700"
      >
        <div class="flex items-center gap-0">
          @for (view of calendarViews; track view) {
            <button
              (click)="setCalendarView(view)"
              class="px-4 py-2.5 text-sm font-medium border-b-2 transition-all -mb-px"
              [class]="
                calendarView() === view
                  ? 'border-purple-600 text-purple-700 dark:text-purple-400'
                  : 'border-transparent text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-200'
              "
            >
              {{ CALENDAR_VIEW_META[view].label }}
            </button>
          }
        </div>
        <div class="flex items-center gap-2 mb-1">
          <div
            class="flex items-center gap-1 bg-gray-100 dark:bg-slate-800 rounded-lg p-1"
          >
            <button
              (click)="navigateCalendar(-1)"
              class="w-7 h-7 flex items-center justify-center rounded-md text-gray-600 dark:text-slate-300 hover:bg-white dark:hover:bg-slate-700 transition-all"
            >
              ‹
            </button>
            <button
              (click)="resetCalendarToToday()"
              class="px-2.5 h-7 text-xs font-medium rounded-md text-gray-600 dark:text-slate-300 hover:bg-white dark:hover:bg-slate-700 transition-all"
              [class.bg-white]="calendarOffset() === 0"
              [class.dark:bg-slate-700]="calendarOffset() === 0"
              [class.shadow-sm]="calendarOffset() === 0"
            >
              Aujourd'hui
            </button>
            <button
              (click)="navigateCalendar(1)"
              class="w-7 h-7 flex items-center justify-center rounded-md text-gray-600 dark:text-slate-300 hover:bg-white dark:hover:bg-slate-700 transition-all"
            >
              ›
            </button>
          </div>
          <button
            (click)="openPeriodForm(null)"
            class="px-3 py-1.5 rounded-lg text-sm font-medium bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-100 dark:hover:bg-indigo-900/50 border border-indigo-200 dark:border-indigo-700 transition-all"
          >
            + Période
          </button>
        </div>
      </div>
    </div>

    <!-- Légende périodes actives -->
    @if (merchantPeriods().length > 0) {
      <div class="flex flex-wrap gap-2 px-6 pt-3">
        @for (period of merchantPeriods(); track period._id) {
          <div
            class="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border"
            [style.border-color]="period.color || '#a78bfa'"
            [style.background-color]="(period.color || '#a78bfa') + '22'"
            [style.color]="period.color || '#7c3aed'"
          >
            {{ period.label }}
            <button
              (click)="openPeriodForm(period)"
              class="opacity-60 hover:opacity-100"
            >
              ✏️
            </button>
            <button
              (click)="deletePeriod(period)"
              class="opacity-60 hover:opacity-100"
            >
              ✕
            </button>
          </div>
        }
      </div>
    }

    <!-- ─── Vue 7 jours ─── -->
    @if (calendarView() === "1w") {
      <div class="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 p-6">
        @for (day of calendarDays; track day.date) {
          <div
            (click)="selectCalendarDay(day)"
            class="relative cursor-pointer rounded-xl border transition-all duration-200 hover:shadow-md overflow-hidden"
            [class]="
              selectedCalendarDay &&
              isSameDay(day.date, selectedCalendarDay.date)
                ? 'border-purple-400 ring-2 ring-purple-500 shadow-lg'
                : day.isToday
                  ? 'border-blue-300 dark:border-blue-600'
                  : day.specialPeriods?.length
                    ? 'border-gray-200 dark:border-slate-700 hover:opacity-90'
                    : 'border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-gray-50 dark:hover:bg-slate-750'
            "
            [style.background-color]="
              day.specialPeriods?.length
                ? (day.specialPeriods![0].color || '#a78bfa') + '22'
                : null
            "
          >
            <div class="p-3">
              <div class="flex items-start justify-between mb-3">
                <div>
                  <div
                    class="text-xs font-medium uppercase tracking-wide text-gray-400 dark:text-slate-500"
                  >
                    {{ day.date | date: "EEE" }}
                  </div>
                  @if (day.isToday) {
                    <div
                      class="mt-0.5 w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center text-sm font-bold"
                    >
                      {{ day.date | date: "d" }}
                    </div>
                  } @else {
                    <div
                      class="mt-0.5 text-2xl font-bold text-gray-900 dark:text-white leading-none"
                    >
                      {{ day.date | date: "d" }}
                    </div>
                  }
                </div>
                <!-- Météo -->
                <div class="flex flex-col items-end gap-1">
                  @if (day.weather?.icon) {
                    <div class="text-right">
                      <div class="text-lg leading-none">
                        {{ day.weather.icon }}
                      </div>
                      <div
                        class="text-xs font-semibold text-gray-600 dark:text-slate-300 mt-0.5"
                      >
                        @if (day.weather.temperatureMin !== undefined) {
                          ↓{{ day.weather.temperatureMin }}° ↑{{
                            day.weather.temperatureMax
                          }}°
                        } @else {
                          {{ day.weather.temperature }}°
                        }
                      </div>
                    </div>
                  }
                </div>
              </div>

              <!-- Événements -->
              @if (day.eventNames?.length) {
                <div class="flex flex-col gap-0.5 mb-1">
                  @for (name of day.eventNames!.slice(0, 2); track name) {
                    <span
                      class="inline-flex items-center gap-0.5 rounded-md bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 px-1.5 py-0.5 text-xs font-medium truncate"
                    >
                      🎤 {{ name }}
                    </span>
                  }
                  @if (day.eventNames!.length > 2) {
                    <span class="text-[10px] text-purple-400"
                      >+{{ day.eventNames!.length - 2 }} autre(s)</span
                    >
                  }
                </div>
              }

              <div class="flex flex-wrap gap-1 mt-1">
                @if (day.isWeekend) {
                  <span
                    class="inline-flex items-center rounded-md bg-green-100 dark:bg-green-500/15 text-green-700 dark:text-green-400 px-1.5 py-0.5 text-xs"
                    >Weekend</span
                  >
                }
                @if (day.alertsCount > 0) {
                  <span
                    class="inline-flex items-center rounded-md bg-red-100 dark:bg-red-500/20 text-red-600 dark:text-red-400 px-1.5 py-0.5 text-xs font-medium"
                    >⚠️ {{ day.alertsCount }}</span
                  >
                }
              </div>

              <!-- Météo détaillée aujourd'hui -->
              @if (day.isToday && day.weather) {
                <div
                  class="mt-2 pt-2 border-t border-blue-100 dark:border-blue-800/40 flex items-center gap-2"
                >
                  <span class="text-2xl">{{ day.weather.icon }}</span>
                  <div class="flex flex-col">
                    @if (day.weather.condition) {
                      <span
                        class="text-xs font-medium text-gray-700 dark:text-slate-200 leading-tight"
                        >{{ day.weather.condition }}</span
                      >
                    }
                    <span class="text-xs text-gray-500 dark:text-slate-400">
                      @if (day.weather.temperatureMin !== undefined) {
                        🌅 {{ day.weather.temperatureMin }}° · ☀️
                        {{ day.weather.temperatureMax }}°
                      } @else {
                        {{ day.weather.temperature }}°C
                      }
                    </span>
                  </div>
                </div>
              }

              <!-- Périodes spéciales -->
              @if (day.specialPeriods?.length) {
                <div class="mt-2 flex flex-col gap-0.5">
                  @for (
                    sp of isDayPeriodExpanded(day)
                      ? day.specialPeriods!
                      : day.specialPeriods!.slice(0, 1);
                    track sp.label
                  ) {
                    <div
                      class="text-xs font-semibold truncate"
                      [style.color]="sp.color || '#7c3aed'"
                    >
                      {{ PERIOD_TYPE_META[sp.type]?.icon }} {{ sp.label }}
                    </div>
                  }
                  @if (day.specialPeriods!.length > 1) {
                    <button
                      (click)="togglePeriodExpand(day, $event)"
                      class="text-[10px] text-purple-500 dark:text-purple-400 hover:underline text-left"
                    >
                      {{
                        isDayPeriodExpanded(day)
                          ? "− Réduire"
                          : "+" + (day.specialPeriods!.length - 1) + " autre(s)"
                      }}
                    </button>
                  }
                </div>
              }
            </div>
          </div>
        }
      </div>
    } @else {
      <!-- ─── Vue multi-semaine (1m / 3m / 1y) ─── -->
      <div class="p-4 sm:p-6">
        <div>
          <!-- En-tête jours -->
          <div class="grid grid-cols-7 gap-1.5 mb-2 min-w-[420px]">
            @for (
              d of ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
              track $index
            ) {
              <div
                class="text-center text-xs font-semibold text-gray-400 dark:text-slate-500 py-1"
              >
                {{ d }}
              </div>
            }
          </div>
          <!-- Grille jours -->
          <div class="grid grid-cols-7 gap-1.5 min-w-[420px]">
            @for (blank of getLeadingBlanks(); track blank) {
              <div></div>
            }
            @for (day of calendarDays; track day.date) {
              <div
                (click)="selectCalendarDay(day)"
                class="relative cursor-pointer rounded-xl border transition-all duration-200 hover:shadow-md overflow-hidden"
                [class]="
                  selectedCalendarDay &&
                  isSameDay(day.date, selectedCalendarDay.date)
                    ? 'border-purple-400 ring-2 ring-purple-500 shadow-lg'
                    : day.isToday
                      ? 'border-blue-300 dark:border-blue-600'
                      : day.specialPeriods?.length
                        ? 'border-gray-200 dark:border-slate-700 hover:opacity-90'
                        : day.isWeekend
                          ? 'border-gray-100 dark:border-slate-700/50 bg-gray-50 dark:bg-slate-800/40 hover:bg-gray-100 dark:hover:bg-slate-700'
                          : 'border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-gray-50 dark:hover:bg-slate-750'
                "
                [style.background-color]="
                  day.specialPeriods?.length
                    ? (day.specialPeriods![0].color || '#a78bfa') + '22'
                    : null
                "
              >
                <!-- Vue 1m : carte riche -->
                <div class="p-1.5 sm:p-2 flex flex-col gap-0.5">
                  <!-- Ligne 1 : jour abrégé + météo -->
                  <div class="flex items-center justify-between">
                    <span
                      class="text-[10px] font-semibold uppercase text-gray-400 dark:text-slate-500 truncate"
                      >{{ day.date | date: "EEE" }}</span
                    >
                    @if (day.weather?.icon) {
                      <span
                        class="text-[10px] text-gray-500 dark:text-slate-400 whitespace-nowrap"
                        >{{ day.weather.icon }}
                        @if (day.weather.temperatureMin !== undefined) {
                          ↓{{ day.weather.temperatureMin }}°↑{{
                            day.weather.temperatureMax
                          }}°
                        } @else {
                          {{ day.weather.temperature }}°
                        }
                      </span>
                    }
                  </div>
                  <!-- Ligne 2 : numéro du jour -->
                  @if (day.isToday) {
                    <span
                      class="w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center leading-none"
                    >
                      {{ day.date | date: "d" }}
                    </span>
                  } @else {
                    <span
                      class="text-lg font-bold text-gray-900 dark:text-white leading-none"
                      >{{ day.date | date: "d" }}</span
                    >
                  }
                  <!-- Ligne 3 : événements -->
                  @if (day.eventNames?.length) {
                    @for (name of day.eventNames!.slice(0, 1); track name) {
                      <div
                        class="flex items-center gap-0.5 rounded bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 px-1 py-0.5 text-[10px] font-medium"
                      >
                        <span>🎤</span>
                        <span class="truncate">{{ name }}</span>
                      </div>
                    }
                    @if (day.eventNames!.length > 1) {
                      <span class="text-[9px] text-purple-400"
                        >+{{ day.eventNames!.length - 1 }}</span
                      >
                    }
                  }
                  <!-- Ligne 4 : alertes -->
                  @if (day.alertsCount > 0) {
                    <div class="text-[10px] font-medium text-red-500">
                      ⚠️ {{ day.alertsCount }} alerte{{
                        day.alertsCount > 1 ? "s" : ""
                      }}
                    </div>
                  }
                  <!-- Ligne 5 : périodes spéciales -->
                  @if (day.specialPeriods?.length) {
                    <div class="flex flex-col gap-0.5">
                      @for (
                        sp of isDayPeriodExpanded(day)
                          ? day.specialPeriods!
                          : day.specialPeriods!.slice(0, 1);
                        track sp.label
                      ) {
                        <div
                          class="text-[10px] font-medium truncate"
                          [style.color]="sp.color || '#7c3aed'"
                        >
                          {{ PERIOD_TYPE_META[sp.type]?.icon }}{{ sp.label }}
                        </div>
                      }
                      @if (day.specialPeriods!.length > 1) {
                        <button
                          (click)="togglePeriodExpand(day, $event)"
                          class="text-[9px] text-purple-400 hover:underline text-left leading-tight"
                        >
                          {{
                            isDayPeriodExpanded(day)
                              ? "−"
                              : "+" + (day.specialPeriods!.length - 1)
                          }}
                        </button>
                      }
                    </div>
                  }
                  <!-- Météo détaillée aujourd'hui (1m) -->
                  @if (day.isToday && day.weather) {
                    <div
                      class="mt-1 pt-1 border-t border-blue-100 dark:border-blue-800/40 flex items-center gap-1"
                    >
                      <span class="text-base">{{ day.weather.icon }}</span>
                      <div class="flex flex-col leading-none">
                        @if (day.weather.condition) {
                          <span
                            class="text-[9px] font-medium text-gray-700 dark:text-slate-200 truncate"
                            >{{ day.weather.condition }}</span
                          >
                        }
                        <span
                          class="text-[9px] text-gray-500 dark:text-slate-400"
                        >
                          @if (day.weather.temperatureMin !== undefined) {
                            🌅 {{ day.weather.temperatureMin }}° · ☀️
                            {{ day.weather.temperatureMax }}°
                          } @else {
                            {{ day.weather.temperature }}°
                          }
                        </span>
                      </div>
                    </div>
                  }
                </div>
              </div>
            }
          </div>
        </div>
      </div>
    }
  </section>

  <!-- Modal : formulaire période spéciale -->
  @if (showPeriodForm()) {
    <app-special-period-form
      [period]="editingPeriod()"
      (saved)="onPeriodSaved()"
      (cancelled)="closePeriodForm()"
    ></app-special-period-form>
  }

  <!-- HERO: Prochain événement -->
  @if (!isEmptyState && nextEvent) {
    <section
      class="bg-gradient-to-br from-purple-600 to-purple-800 text-white rounded-xl shadow-lg dark:shadow-slate-900/50 p-5"
    >
      <!-- Ligne principale -->
      <div class="flex items-start justify-between gap-4">
        <div class="min-w-0 flex-1">
          <p class="text-xs font-medium text-purple-200 mb-1">
            Prochain événement
          </p>
          <h2 class="text-xl font-bold">{{ nextEvent.name }}</h2>
          <div
            class="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm text-purple-100"
          >
            <span>{{ getDaysUntilLabel(nextEvent.eventDate) }}</span>
            @if (nextEvent.location) {
              <span>📍 {{ nextEvent.location }}</span>
            }
            @if (nextEvent.startTime) {
              <span
                >⏰ {{ nextEvent.startTime
                }}{{ nextEvent.endTime ? " – " + nextEvent.endTime : "" }}</span
              >
            }
          </div>
        </div>
        <button
          (click)="viewEvent(nextEvent.id)"
          class="shrink-0 mt-1 px-4 py-2 text-sm bg-white dark:bg-slate-800 text-purple-700 dark:text-purple-300 font-semibold rounded-lg hover:bg-purple-50 dark:bg-purple-900/20 transition"
        >
          Recommandations →
        </button>
      </div>

      <!-- Détail météo étendu — uniquement si événement aujourd'hui ou demain -->
      @if (
        isNextEventImminent && nextEventWeather && nextEventWeather.available
      ) {
        <div class="mt-4 pt-4 border-t border-purple-500/40">
          <p
            class="text-xs font-semibold text-purple-300 uppercase tracking-wide mb-3"
          >
            Météo prévue lors de l'événement
          </p>
          <div class="flex flex-wrap items-center gap-4">
            <!-- Icône + condition -->
            <div class="flex items-center gap-2">
              <span class="text-4xl leading-none">{{
                getNextEventWeatherIcon()
              }}</span>
              <div>
                <p class="text-sm font-semibold leading-tight">
                  {{ nextEventWeather.condition }}
                </p>
                <p class="text-xs text-purple-200">Source : Open-Meteo</p>
              </div>
            </div>
            <!-- Stats -->
            <div class="flex gap-4 text-sm">
              <div class="text-center">
                <p class="text-purple-300 text-xs">Min</p>
                <p class="font-bold">
                  {{ nextEventWeather.temperatureMin | number: "1.0-0" }}°C
                </p>
              </div>
              <div class="text-center">
                <p class="text-purple-300 text-xs">Max</p>
                <p class="font-bold">
                  {{ nextEventWeather.temperatureMax | number: "1.0-0" }}°C
                </p>
              </div>
              @if (nextEventWeather.precipitationSum > 0) {
                <div class="text-center">
                  <p class="text-purple-300 text-xs">Précip.</p>
                  <p class="font-bold">
                    {{ nextEventWeather.precipitationSum | number: "1.0-1" }}
                    mm
                  </p>
                </div>
              }
            </div>
            <!-- Alerte mauvais temps -->
            @if (getNextEventWeatherIsAlert()) {
              <div
                class="flex items-center gap-1.5 px-3 py-1.5 bg-orange-500/30 border border-orange-400/40 rounded-lg text-xs font-medium text-orange-100"
              >
                <span>⚠️</span>
                <span>Météo défavorable — adapter les quantités</span>
              </div>
            }
          </div>
        </div>
      }

      <!-- Prévision simplifiée — événements au-delà de J+1 -->
      @if (
        !isNextEventImminent && nextEventWeather && nextEventWeather.available
      ) {
        <div
          class="mt-4 pt-4 border-t border-purple-500/40 flex items-center gap-3 text-sm"
        >
          <span class="text-2xl leading-none">{{
            getNextEventWeatherIcon()
          }}</span>
          <div>
            <p class="text-xs text-purple-300 mb-0.5">
              Prévision météo (indicatif)
            </p>
            <p class="font-medium text-purple-100">
              {{ getNextEventWeatherLabel() }}
              @if (nextEventWeather.precipitationSum > 0) {
                <span class="text-purple-300">
                  · 💧
                  {{ nextEventWeather.precipitationSum | number: "1.0-1" }}
                  mm</span
                >
              }
            </p>
          </div>
          @if (getNextEventWeatherIsAlert()) {
            <span
              class="ml-auto text-xs px-2 py-1 bg-orange-500/30 text-orange-100 rounded-full"
              >⚠️ Défavorable</span
            >
          }
        </div>
      }

      <!-- Skeleton : météo en cours de chargement -->
      @if (hasNextEventCoords && nextEventWeather === null) {
        <div
          class="mt-4 pt-4 border-t border-purple-500/40 flex items-center gap-3"
        >
          <div
            class="w-8 h-8 rounded-full bg-purple-500/30 animate-pulse"
          ></div>
          <div class="space-y-1">
            <div class="w-32 h-3 rounded bg-purple-500/30 animate-pulse"></div>
            <div class="w-20 h-2 rounded bg-purple-500/20 animate-pulse"></div>
          </div>
        </div>
      }
    </section>
  }
  <!-- ═══ PHASE 2: RECOMMANDATIONS TRANSPARENTES ═══ -->
  @if (!isEmptyState && nextEventRecos && nextEventRecos.products.length > 0) {
    <section
      class="bg-white dark:bg-slate-800 rounded-xl shadow-sm dark:shadow-slate-900/30 p-4 md:p-6"
    >
      <div class="flex items-center justify-between mb-4">
        <h2
          class="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2"
        >
          <span>🧮</span> Recommandations IA — {{ nextEventRecos.eventName }}
        </h2>
        <button
          (click)="toggleRecoDetail()"
          class="text-sm text-purple-600 hover:underline font-medium"
        >
          {{ showRecoDetail ? "Masquer les détails" : "Voir la formule" }}
        </button>
      </div>
      <!-- Formule visuelle (dépliable) -->
      @if (showRecoDetail) {
        <div
          class="mb-4 p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800"
        >
          <p class="text-sm font-medium text-purple-900 mb-2">
            Comment ça fonctionne ?
          </p>
          <div
            class="flex items-center gap-2 text-sm text-purple-800 dark:text-purple-300 flex-wrap"
          >
            <span class="px-2 py-1 bg-white dark:bg-slate-800 rounded font-mono"
              >Base</span
            >
            <span>×</span>
            <span class="px-2 py-1 bg-white dark:bg-slate-800 rounded font-mono"
              >Coef Event</span
            >
            <span>×</span>
            <span class="px-2 py-1 bg-white dark:bg-slate-800 rounded font-mono"
              >Coef Météo</span
            >
            <span>×</span>
            <span class="px-2 py-1 bg-white dark:bg-slate-800 rounded font-mono"
              >Coef Durée</span
            >
            <span>=</span>
            <span class="px-2 py-1 bg-purple-600 text-white rounded font-bold"
              >Recommandé</span
            >
          </div>
          <p class="text-xs text-purple-600 mt-2">
            Chaque coefficient est ajusté automatiquement en fonction de vos
            ventes passées.
          </p>
        </div>
      }
      <!-- Tableau produits -->
      <div class="space-y-3">
        @for (p of nextEventRecos.products; track p.productName) {
          <div
            class="p-3 bg-gray-50 dark:bg-slate-900 rounded-lg border border-gray-200 dark:border-slate-700"
          >
            <div class="flex items-center justify-between mb-2">
              <div>
                <p class="font-semibold text-gray-900 dark:text-white">
                  {{ p.productName }}
                </p>
                <p class="text-xs text-gray-500 dark:text-gray-400">
                  {{ p.unit }}
                </p>
              </div>
              <span
                class="text-xl font-bold text-purple-700 dark:text-purple-300"
                >{{ p.recommended }}</span
              >
            </div>
            <!-- Barre fourchette -->
            <div
              class="relative h-2 bg-gray-200 dark:bg-slate-700 rounded-full mb-1"
            >
              <div
                class="absolute h-2 bg-purple-200 rounded-full"
                [style.left.%]="0"
                [style.width.%]="100"
              ></div>
              <div
                class="absolute h-2 bg-purple-500 rounded-full"
                [style.left.%]="getRangePercentage(p, p.rangeLow)"
                [style.width.%]="
                  getRangePercentage(p, p.rangeHigh) -
                  getRangePercentage(p, p.rangeLow)
                "
              ></div>
              <div
                class="absolute w-3 h-3 bg-purple-700 rounded-full -top-0.5 border-2 border-white shadow"
                [style.left.%]="getRangePercentage(p, p.recommended)"
              ></div>
            </div>
            <div
              class="flex justify-between text-xs text-gray-500 dark:text-gray-400"
            >
              <span>{{ p.rangeLow }}</span>
              <span class="font-medium text-purple-700 dark:text-purple-300"
                >{{ p.recommended }} {{ p.unit }}</span
              >
              <span>{{ p.rangeHigh }}</span>
            </div>
            <!-- Détail coefficients (si déplié) -->
            @if (showRecoDetail) {
              <div class="mt-2 flex gap-2 flex-wrap">
                <span
                  class="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-200 dark:bg-slate-700 rounded text-xs"
                >
                  Base: {{ p.coefficients.base }}
                </span>
                <span
                  class="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded text-xs"
                >
                  Event: ×{{ p.coefficients.eventType }}
                </span>
                <span
                  class="inline-flex items-center gap-1 px-2 py-0.5 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 rounded text-xs"
                >
                  Météo: ×{{ p.coefficients.weather }}
                </span>
                <span
                  class="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded text-xs"
                >
                  Durée: ×{{ p.coefficients.duration }}
                </span>
                <span
                  class="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded text-xs font-bold"
                >
                  = {{ getFormulaResult(p) }}
                </span>
              </div>
            }
          </div>
        }
      </div>
    </section>
  }
  <!-- ═══ PHASE 2: BOUCLE D'APPRENTISSAGE ═══ -->
  @if (!isEmptyState && precisionHistory.length >= 3) {
    <section
      class="bg-white dark:bg-slate-800 rounded-xl shadow-sm dark:shadow-slate-900/30 p-4 md:p-6"
    >
      <h2
        class="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2"
      >
        <span>📈</span> Progression de la précision IA
      </h2>
      <!-- Message motivant -->
      @if (precisionGain > 0) {
        <div
          class="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg flex items-center gap-3"
        >
          <span class="text-2xl">🎉</span>
          <p class="text-sm text-green-800 dark:text-green-300">
            <span class="font-bold">+{{ precisionGain }}% de précision</span>
            en {{ precisionEventCount }} événements ! Continuez à saisir vos
            ventes pour améliorer les recommandations.
          </p>
        </div>
      }
      <!-- Courbe simplifiée (barres horizontales) -->
      <div class="space-y-2">
        @for (
          point of precisionHistory;
          track point.eventId;
          let i = $index;
          let last = $last
        ) {
          <div class="flex items-center gap-3">
            <span
              class="text-xs text-gray-500 dark:text-gray-400 w-16 text-right truncate"
              [title]="point.eventName"
            >
              {{
                point.eventName.length > 8
                  ? point.eventName.substring(0, 8) + "…"
                  : point.eventName
              }}
            </span>
            <div
              class="flex-1 h-4 bg-gray-100 dark:bg-slate-800 rounded-full overflow-hidden"
            >
              <div
                class="h-full rounded-full transition-all duration-500"
                [ngClass]="getPrecisionColor(point.precision)"
                [style.width]="getPrecisionBarWidth(point)"
              ></div>
            </div>
            <span
              class="text-xs font-bold w-10 text-right"
              [ngClass]="{
                'text-green-600 dark:text-green-400': point.precision >= 85,
                'text-yellow-600':
                  point.precision >= 70 && point.precision < 85,
                'text-red-600 dark:text-red-400': point.precision < 70,
              }"
            >
              {{ point.precision }}%
            </span>
          </div>
        }
      </div>
      <p class="text-xs text-gray-500 dark:text-gray-400 mt-3 text-center">
        Basé sur {{ precisionEventCount }} événements analysés
      </p>
    </section>
  }
  <!-- ═══ PHASE 3: CHECKLIST PRÉ-ÉVÉNEMENT ═══ -->
  @if (
    !isEmptyState && nextEventChecklist && nextEventChecklist.items.length > 0
  ) {
    <section
      class="bg-white dark:bg-slate-800 rounded-xl shadow-sm dark:shadow-slate-900/30 p-4 md:p-6 relative"
    >
      @if (!planFeatureService.canAccess(FEATURES.PREPARATION_CHECKLIST)) {
        <!-- Version verrouillée: titre + bouton upgrade -->
        <div class="flex items-center justify-between">
          <h2
            class="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2"
          >
            <span>✅</span> Checklist — {{ nextEventChecklist.eventName }}
          </h2>
          <a
            routerLink="/subscription"
            class="inline-flex items-center gap-1.5 px-3 py-1.5 bg-yellow-400 text-yellow-900 text-xs font-bold rounded-full hover:bg-yellow-300 transition"
          >
            🔒 Pro
          </a>
        </div>
      } @else {
        <!-- Version débloquée: contenu complet -->
        <div class="flex items-center justify-between mb-4">
          <h2
            class="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2"
          >
            <span>✅</span> Checklist — {{ nextEventChecklist.eventName }}
          </h2>
          <span
            class="text-sm font-medium"
            [ngClass]="
              checklistProgress === 100
                ? 'text-green-600 dark:text-green-400'
                : 'text-gray-500 dark:text-gray-400'
            "
          >
            {{ checklistCompletedCount }}/{{ nextEventChecklist.items.length }}
          </span>
        </div>
        <!-- Barre de progression -->
        <div
          class="h-2 bg-gray-200 dark:bg-slate-700 rounded-full mb-4 overflow-hidden"
        >
          <div
            class="h-full rounded-full transition-all duration-300"
            [ngClass]="
              checklistProgress === 100 ? 'bg-green-500' : 'bg-purple-500'
            "
            [style.width.%]="checklistProgress"
          ></div>
        </div>
        <!-- Filtres catégorie -->
        <div class="flex gap-1 overflow-x-auto pb-2 mb-3">
          @for (cat of checklistCategories; track cat.key) {
            <button
              (click)="setChecklistFilter(cat.key)"
              class="flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-all"
              [ngClass]="
                checklistFilter === cat.key
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-100 dark:bg-slate-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-slate-600 dark:bg-slate-700'
              "
            >
              {{ cat.icon }} {{ cat.label }}
            </button>
          }
        </div>
        <!-- Items -->
        <div class="space-y-1.5">
          @for (item of checklistItems; track item.id) {
            <div
              (click)="toggleChecklistItem(item)"
              class="flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all"
              [ngClass]="
                item.completed
                  ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
                  : 'bg-gray-50 dark:bg-slate-900 border border-gray-200 dark:border-slate-700 hover:bg-gray-100 dark:hover:bg-slate-700 dark:bg-slate-800'
              "
            >
              <!-- Checkbox -->
              <div
                class="flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-all"
                [ngClass]="
                  item.completed
                    ? 'bg-green-500 border-green-500'
                    : 'border-gray-400 dark:border-slate-500'
                "
              >
                @if (item.completed) {
                  <svg
                    class="w-3 h-3 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="3"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                }
              </div>
              <!-- Label -->
              <div class="flex-1 min-w-0">
                <p
                  class="text-sm"
                  [ngClass]="
                    item.completed
                      ? 'text-gray-500 dark:text-gray-400 line-through'
                      : 'text-gray-900 dark:text-white'
                  "
                >
                  {{ item.label }}
                </p>
                <div class="flex items-center gap-2 mt-0.5">
                  <span class="text-xs text-gray-400">{{
                    getCategoryIcon(item.category)
                  }}</span>
                  @if (item.contextual) {
                    <span
                      class="inline-flex items-center px-1.5 py-0.5 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 rounded text-xs"
                    >
                      🌦️ Contextuel
                    </span>
                  }
                </div>
              </div>
            </div>
          }
        </div>
        <!-- Message si tout coché -->
        @if (checklistProgress === 100) {
          <div
            class="mt-3 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-center"
          >
            <span class="text-2xl">🎯</span>
            <p
              class="text-sm font-medium text-green-800 dark:text-green-300 mt-1"
            >
              Tout est prêt pour {{ nextEventChecklist.eventName }} !
            </p>
          </div>
        }
      }
    </section>
  }
  <!-- À COMPLÉTER -->
  @if (hasCompletedWithoutSales) {
    <section
      class="bg-orange-50 dark:bg-orange-900/20 border-l-4 border-orange-500 rounded-lg p-4 md:p-6"
    >
      <div class="flex items-start gap-4">
        <span class="text-3xl">⚠️</span>
        <div class="flex-1">
          <h2 class="text-lg font-bold text-orange-900 mb-2">
            {{ completedCount }} événement{{ completedCount > 1 ? "s" : "" }}
            à compléter
          </h2>
          <p class="text-sm text-orange-800 dark:text-orange-300 mb-4">
            Saisissez vos ventes pour améliorer la précision des
            recommandations.
          </p>
          <div class="space-y-2">
            @for (event of eventsByStatus.completed; track event.id) {
              <div
                class="flex items-center justify-between p-3 bg-white dark:bg-slate-800 rounded-lg border border-orange-200 dark:border-orange-800"
              >
                <div class="flex items-center gap-3">
                  <span class="text-xl">{{
                    getEventIcon(event.eventType)
                  }}</span>
                  <div>
                    <p class="font-medium text-gray-900 dark:text-white">
                      {{ event.name }}
                    </p>
                    <p class="text-xs text-gray-500 dark:text-gray-400">
                      {{ event.location }} •
                      {{ formatEventDate(event.eventDate) }}
                    </p>
                  </div>
                </div>
                <button
                  (click)="enterSalesEntry(event.id)"
                  class="px-4 py-2 bg-orange-600 text-white font-medium rounded-lg hover:bg-orange-700 transition text-sm"
                >
                  Saisir mes ventes
                </button>
              </div>
            }
          </div>
        </div>
      </div>
    </section>
  }
  <!-- LAYOUT 2 COLONNES (desktop) -->
  <div
    [ngClass]="
      isEmptyState ? 'flex flex-col gap-6' : 'grid lg:grid-cols-2 gap-6'
    "
  >
    <!-- COLONNE GAUCHE - uniquement si événements existent -->
    @if (!isEmptyState) {
      <div class="space-y-6">
        <!-- Mes événements -->
        <section
          class="bg-white dark:bg-slate-800 rounded-xl shadow-sm dark:shadow-slate-900/30 p-4 md:p-6"
        >
          <h2
            class="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2"
          >
            <span>📅</span> Mes événements
          </h2>
          <!-- À venir -->
          @if (eventsByStatus.planned.length > 0) {
            <div class="mb-4">
              <h3
                class="text-xs font-semibold text-purple-700 dark:text-purple-300 uppercase tracking-wide mb-2"
              >
                À venir ({{ eventsByStatus.planned.length }})
              </h3>
              <div class="">
                <div
                  style="
                    max-height: 640px;
                    overflow-y: auto;
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                  "
                >
                  @for (event of eventsByStatus.planned; track event.id) {
                    <div
                      class="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800 hover:border-purple-400 transition"
                      [class.ring-2]="highlightedEventId === event.id"
                      [class.ring-indigo-300]="highlightedEventId === event.id"
                    >
                      <div class="flex items-start justify-between mb-1">
                        <div class="flex items-start gap-3">
                          <span class="text-xl">{{
                            getEventIcon(event.eventType)
                          }}</span>
                          <div>
                            <p
                              class="font-medium text-gray-900 dark:text-white"
                            >
                              {{ event.name }}
                            </p>
                            <p class="text-xs text-gray-500 dark:text-gray-400">
                              {{ formatLocation(event.location) }} •
                              {{ formatEventDate(event.eventDate) }}
                            </p>
                            <p class="text-xs text-gray-500 dark:text-gray-400">
                              {{ event.startTime
                              }}{{ event.endTime ? " - " + event.endTime : "" }}
                            </p>
                          </div>
                        </div>
                        <div class="text-right flex flex-col items-end gap-1">
                          <span
                            class="inline-block text-xs font-medium text-purple-600"
                            >{{ getDaysUntilLabel(event.eventDate) }}</span
                          >
                        </div>
                      </div>
                      <!-- Marge moyenne estimée & affluence -->
                      @if (event.estimatedMargin) {
                        <div
                          class="flex items-center gap-4 mt-2 pt-2 border-t border-purple-100 text-xs"
                        >
                          <span class="text-gray-600 dark:text-gray-400"
                            >📈 Marge moyenne:
                            <strong class="text-green-600 dark:text-green-400"
                              >{{
                                ((event.estimatedMargin.low || 0) +
                                  (event.estimatedMargin.high || 0)) /
                                  2 | number: "1.0-0"
                              }}€</strong
                            ></span
                          >
                          <span class="text-gray-600 dark:text-gray-400">
                            👥 Affluence:
                            <strong>{{
                              event.estimatedAttendance ||
                                event.expectedAttendance ||
                                "?"
                            }}</strong>
                          </span>
                        </div>
                      }
                      <div
                        class="mt-2 text-xs text-gray-600 dark:text-gray-400"
                      >
                        Status: {{ event.status }}
                      </div>
                      <!-- Actions bas-droite -->
                      <div class="flex justify-end items-center gap-2 mt-1">
                        <button
                          (click)="viewEvent(event.id)"
                          class="text-xs font-medium text-purple-600 hover:text-purple-800 dark:text-purple-300 border border-purple-300 hover:border-purple-500 rounded-lg px-3 py-1 transition bg-white dark:bg-slate-800"
                        >
                          📋 Détails
                        </button>
                        <button
                          (click)="deleteEvent(event, $event)"
                          title="Supprimer cet événement"
                          class="text-red-400 hover:text-red-600 dark:text-red-400 transition text-lg leading-none"
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                  }
                </div>
              </div>
            </div>
          }
          <!-- Analysés -->
          @if (eventsByStatus.analyzed.length > 0) {
            <div>
              <h3
                class="text-xs font-semibold text-blue-700 dark:text-blue-300 uppercase tracking-wide mb-2"
              >
                Analysés ({{ eventsByStatus.analyzed.length }})
              </h3>
              <div class="space-y-2">
                @for (
                  event of eventsByStatus.analyzed.slice(0, 2);
                  track event.id
                ) {
                  <div
                    class="flex items-center justify-between p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800 hover:border-blue-400 dark:border-blue-700 transition cursor-pointer"
                    (click)="router.navigate(['/events', event.id, 'rapport'])"
                  >
                    <div class="flex items-center gap-3">
                      <span class="text-xl">✅</span>
                      <div>
                        <p class="font-medium text-gray-900 dark:text-white">
                          {{ event.name }}
                        </p>
                        <p class="text-xs text-gray-500 dark:text-gray-400">
                          {{ event.location }}
                        </p>
                      </div>
                    </div>
                    <span
                      class="text-xs font-medium text-blue-600 dark:text-blue-400"
                      >Voir rapport →</span
                    >
                  </div>
                }
              </div>
            </div>
          }
          <!-- État vide -->
          @if (
            totalPlanned === 0 &&
            eventsByStatus.active.length === 0 &&
            eventsByStatus.analyzed.length === 0
          ) {
            <div class="text-center py-8 text-gray-500 dark:text-gray-400">
              <p class="text-sm">Aucun événement créé.</p>
              <button
                (click)="createEvent()"
                class="mt-3 px-4 py-2 bg-purple-600 text-white font-medium rounded-lg hover:bg-purple-700"
              >
                Créer mon premier événement
              </button>
            </div>
          }
        </section>
        <!-- Insights IA -->
        <section
          class="bg-white dark:bg-slate-800 rounded-xl shadow-sm dark:shadow-slate-900/30 p-4 md:p-6"
        >
          <h2
            class="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2"
          >
            <span>🤖</span> Insights IA
          </h2>
          @if (insights.length > 0) {
            <div class="space-y-3">
              @for (insight of insights; track insight.id) {
                <div
                  class="p-4 rounded-lg border-2"
                  [ngClass]="getInsightClasses(insight.type)"
                >
                  <div class="flex items-start gap-3">
                    <span class="text-2xl">{{ insight.icon }}</span>
                    <div class="flex-1">
                      <p class="font-bold text-gray-900 dark:text-white">
                        {{ insight.title }}
                      </p>
                      <p class="text-sm text-gray-700 dark:text-gray-300 mt-1">
                        {{ insight.message }}
                      </p>
                    </div>
                  </div>
                </div>
              }
            </div>
          }
          @if (insights.length === 0) {
            <div
              class="text-center py-6 text-gray-500 dark:text-gray-400 text-sm"
            >
              Complétez plus d'événements pour obtenir des insights
              personnalisés.
            </div>
          }
        </section>
        <!-- Top événements -->
        <section
          class="bg-white dark:bg-slate-800 rounded-xl shadow-sm dark:shadow-slate-900/30 p-4 md:p-6"
        >
          <h2
            class="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2"
          >
            <span>🏆</span> Top événements
          </h2>
          @if (topEvents.length > 0) {
            <div class="space-y-2">
              @for (event of topEvents; track event.id; let i = $index) {
                <div
                  class="flex items-center gap-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800 hover:border-blue-400 dark:border-blue-700 transition cursor-pointer"
                  (click)="router.navigate(['/events', event.id, 'rapport'])"
                >
                  <span class="text-2xl font-bold text-gray-400">{{
                    i + 1
                  }}</span>
                  <div class="flex-1 min-w-0">
                    <p
                      class="font-medium text-gray-900 dark:text-white truncate"
                    >
                      {{ event.name }}
                    </p>
                    <p class="text-xs text-gray-400">
                      {{ event.date | date: "d MMM yyyy" : "" : "fr" }}
                    </p>
                    <div class="flex items-center gap-3 mt-1">
                      @if (event.estimatedProfit !== null) {
                        <span class="text-xs text-gray-400">
                          IA prévu : {{ event.estimatedProfit }}€
                        </span>
                        <span class="text-gray-300">|</span>
                      }
                      <span class="text-xs">
                        Réalisé :
                        <span
                          [class]="
                            event.estimatedProfit !== null &&
                            event.profit >= event.estimatedProfit
                              ? 'font-semibold text-green-600 dark:text-green-400'
                              : 'font-semibold text-orange-500'
                          "
                        >
                          {{ event.profit }}€
                        </span>
                        @if (
                          event.estimatedProfit !== null &&
                          event.estimatedProfit > 0
                        ) {
                          <span class="text-gray-400">
                            ({{
                              (event.profit / event.estimatedProfit) * 100
                                | number: "1.0-0"
                            }}%)
                          </span>
                        }
                      </span>
                    </div>
                  </div>
                  <span
                    class="text-xs font-medium text-blue-600 dark:text-blue-400 shrink-0"
                    >Voir rapport →</span
                  >
                </div>
              }
            </div>
          }
          @if (topEvents.length === 0) {
            <div
              class="text-center py-6 text-gray-500 dark:text-gray-400 text-sm"
            >
              Complétez au moins 3 événements pour voir votre top.
            </div>
          }
        </section>
      </div>
    }
    <!-- COLONNE DROITE -->
    <div class="space-y-6">
      <!-- Carte + opportunités : uniquement quand événements existent -->
      @if (!isEmptyState) {
        <div class="relative">
          <!-- 🗺️ Carte — Zone d'activité -->
          @if (merchantLocation) {
            <div class="relative">
              @if (!planFeatureService.canAccess(FEATURES.ACTIVITY_ZONE)) {
                <!-- Overlay bloquant TOUTES les interactions (y compris Leaflet) -->
                <div
                  class="absolute inset-0 z-20 cursor-default"
                  style="background: transparent"
                ></div>
              }
              <div
                [style.opacity]="
                  !planFeatureService.canAccess(FEATURES.ACTIVITY_ZONE)
                    ? '0.6'
                    : '1'
                "
              >
                <app-map-widget
                  [merchantLocation]="merchantLocation"
                  [events]="mapEvents"
                  [opportunities]="filteredOpportunities"
                  [initialFollowedIds]="followedOpportunityIds"
                  [oppCountOverride]="filteredOpportunities.length"
                  (radiusChanged)="onRadiusChanged($event)"
                  (opportunityFollowed)="onOpportunityFollowed($event)"
                >
                </app-map-widget>
              </div>
              <!-- Titre à opacité normale + badge 🔒 Pro positionné -->
              @if (!planFeatureService.canAccess(FEATURES.ACTIVITY_ZONE)) {
                <div
                  class="absolute top-0 left-0 right-0 z-30 flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-slate-700 bg-white dark:bg-slate-800 rounded-t-xl"
                >
                  <h2
                    class="text-base font-bold text-gray-900 dark:text-white flex items-center gap-2"
                  >
                    <span>🗺️</span> Ma zone d'activité
                  </h2>
                  <div class="flex items-center gap-2">
                    <a
                      routerLink="/subscription"
                      class="inline-flex items-center gap-1 px-2.5 py-1 bg-yellow-400 text-yellow-900 text-xs font-bold rounded-full hover:bg-yellow-300 transition"
                    >
                      🔒 Pro
                    </a>
                    <span
                      class="p-1.5 rounded-lg text-gray-400"
                      title="Recentrer"
                      >🎯</span
                    >
                    <span
                      class="p-1.5 rounded-lg text-gray-400"
                      title="Plein écran"
                      >⛶</span
                    >
                  </div>
                </div>
              }
            </div>
          }
          <!-- 💡 Opportunités recommandées -->
          <ng-container *ngTemplateOutlet="oppSection"></ng-container>
        </div>
      }
      <!-- ng-template partagé (toujours parsé car hors *ngIf) -->
      <ng-template #oppSection>
        @if (opportunities.length > 0) {
          <section
            class="bg-white dark:bg-slate-800 rounded-xl shadow-sm dark:shadow-slate-900/30 p-4 md:p-6"
          >
            <h2
              class="text-lg font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2"
            >
              <span>💡</span> Opportunités recommandées
              <span class="ml-auto flex items-center gap-2">
                @if (!planFeatureService.canAccess(FEATURES.ACTIVITY_ZONE)) {
                  <a
                    routerLink="/subscription"
                    class="inline-flex items-center gap-1 px-2.5 py-1 bg-yellow-400 text-yellow-900 text-xs font-bold rounded-full hover:bg-yellow-300 transition"
                  >
                    🔒 Pro
                  </a>
                }
                <span
                  class="text-sm font-normal text-gray-500 dark:text-gray-400"
                  >{{ filteredOpportunities.length }} à venir</span
                >
              </span>
            </h2>
            <div
              [style.opacity]="
                !planFeatureService.canAccess(FEATURES.ACTIVITY_ZONE)
                  ? '0.6'
                  : '1'
              "
              [style.pointer-events]="
                !planFeatureService.canAccess(FEATURES.ACTIVITY_ZONE)
                  ? 'none'
                  : 'auto'
              "
            >
              <!-- Filtres -->
              <div class="flex flex-wrap gap-2 mb-4">
                <!-- Filtre période -->
                <select
                  [(ngModel)]="oppFilterPeriod"
                  (change)="applyOpportunityFilters()"
                  class="text-xs px-3 py-1.5 border border-gray-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="all">📅 Toutes les dates</option>
                  <optgroup label="── Par semaine ──">
                    @for (w of weekOptions; track w.value) {
                      <option [value]="w.value">
                        {{ w.label }}
                      </option>
                    }
                  </optgroup>
                  <optgroup label="── Vue large ──">
                    <option value="month">Ce mois</option>
                    <option value="3months">3 prochains mois</option>
                    <option value="6months">6 prochains mois</option>
                    <option value="weekend">Week-ends uniquement</option>
                  </optgroup>
                </select>
                <!-- Filtre rentabilité -->
                <select
                  [(ngModel)]="oppFilterProfitability"
                  (change)="applyOpportunityFilters()"
                  class="text-xs px-3 py-1.5 border border-gray-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="all">💰 Toute rentabilité</option>
                  <option value="high">🟢 Très rentable</option>
                  <option value="medium">🟡 Rentable</option>
                  <option value="low">🟠 Faible</option>
                </select>
                <!-- Filtre type -->
                <select
                  [(ngModel)]="oppFilterType"
                  (change)="applyOpportunityFilters()"
                  class="text-xs px-3 py-1.5 border border-gray-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="all">🎪 Tous types</option>
                  <option value="festival">Festival</option>
                  <option value="concert">Concert</option>
                  <option value="conference">Conférence</option>
                  <option value="exposition">Exposition</option>
                  <option value="sport">Sport</option>
                  <option value="autre">Autre</option>
                </select>
                <!-- Tri -->
                <div class="flex items-center gap-1">
                  <select
                    [(ngModel)]="oppSortBy"
                    (change)="onSortByChange()"
                    class="text-xs px-3 py-1.5 border border-gray-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="date">📅 Date</option>
                    <option value="margin">💰 Marge</option>
                    <option value="score">🎯 Score</option>
                  </select>
                  <button
                    (click)="
                      oppSortDir = oppSortDir === 'asc' ? 'desc' : 'asc';
                      applyOpportunityFilters()
                    "
                    title="Inverser le tri"
                    class="px-2.5 py-1.5 border border-gray-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 hover:bg-gray-50 dark:hover:bg-slate-700 dark:bg-slate-900 text-sm leading-none transition"
                  >
                    {{ oppSortDir === "asc" ? "↑" : "↓" }}
                  </button>
                </div>
                <!-- Reset -->
                @if (
                  oppFilterPeriod !== "all" ||
                  oppFilterProfitability !== "all" ||
                  oppFilterType !== "all" ||
                  oppSortBy !== "date" ||
                  oppSortDir !== "asc"
                ) {
                  <button
                    (click)="resetOpportunityFilters()"
                    class="text-xs px-3 py-1.5 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 dark:bg-slate-800 rounded-lg transition"
                  >
                    ✕ Réinitialiser
                  </button>
                }
              </div>
              <div class="space-y-3 max-h-[600px] overflow-y-auto">
                @for (opp of filteredOpportunities; track opp.id) {
                  <div
                    class="border rounded-lg p-4 transition-colors"
                    [ngClass]="{
                      'border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 hover:border-green-400 dark:border-green-700':
                        opp.profitabilityLevel === 'high',
                      'border-yellow-200 dark:border-yellow-800 bg-yellow-50 dark:bg-yellow-900/20 hover:border-yellow-400 dark:border-yellow-700':
                        opp.profitabilityLevel === 'medium',
                      'border-orange-200 dark:border-orange-800 bg-orange-50 dark:bg-orange-900/20 hover:border-orange-400':
                        opp.profitabilityLevel === 'low',
                    }"
                  >
                    <!-- En-tête avec indicateur viabilité -->
                    <div class="flex items-start justify-between mb-2">
                      <div>
                        <div class="flex items-center gap-2">
                          <span
                            class="w-3 h-3 rounded-full flex-shrink-0"
                            [ngClass]="{
                              'bg-green-500': opp.profitabilityLevel === 'high',
                              'bg-yellow-400':
                                opp.profitabilityLevel === 'medium',
                              'bg-orange-500': opp.profitabilityLevel === 'low',
                            }"
                          ></span>
                          <h3
                            class="font-semibold text-gray-900 dark:text-white"
                          >
                            {{ opp.name }}
                          </h3>
                        </div>
                        <p
                          class="text-sm text-gray-500 dark:text-gray-400 ml-5"
                        >
                          📍 {{ opp.location }} · {{ opp.distance }}
                        </p>
                      </div>
                      <span
                        class="px-2 py-1 rounded-full text-xs font-medium"
                        [class.bg-green-100]="opp.activityMatch >= 80"
                        [class.text-green-700]="opp.activityMatch >= 80"
                        [class.bg-blue-100]="
                          opp.activityMatch >= 60 && opp.activityMatch < 80
                        "
                        [class.text-blue-700]="
                          opp.activityMatch >= 60 && opp.activityMatch < 80
                        "
                        [class.bg-gray-100]="opp.activityMatch < 60"
                        [class.text-gray-600]="opp.activityMatch < 60"
                      >
                        {{ opp.activityMatchLabel }}
                      </span>
                    </div>
                    <!-- Infos événement -->
                    <div
                      class="flex flex-wrap gap-3 text-sm text-gray-600 dark:text-gray-400 mb-3"
                    >
                      <span>📅 {{ opp.dateFormatted }}</span>
                      @if (opp.timeRange) {
                        <span>🕐 {{ opp.timeRange }}</span>
                      }
                      <span>{{ opp.eventTypeLabel }}</span>
                    </div>
                    <!-- Indicateur de viabilité -->
                    @if (!opp.isViable) {
                      <div
                        class="mb-3 px-3 py-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-sm"
                      >
                        <span
                          class="font-medium text-amber-700 dark:text-amber-300"
                          >⚠️ Rentabilité incertaine</span
                        >
                        <span class="text-amber-600">
                          — CA minimum conseillé : {{ opp.minViableCA }}€</span
                        >
                      </div>
                    }
                    <!-- Métriques principales -->
                    <div
                      class="grid grid-cols-2 gap-2 bg-gray-50 dark:bg-slate-900 rounded-lg p-3 mb-2"
                    >
                      <div class="text-center">
                        <div class="text-xs text-gray-500 dark:text-gray-400">
                          💰 CA estimé
                        </div>
                        <div
                          class="font-bold"
                          [class.text-green-600]="opp.isViable"
                          [class.text-amber-600]="!opp.isViable"
                        >
                          {{ opp.estimatedRevenue.low }}–{{
                            opp.estimatedRevenue.high
                          }}€
                        </div>
                      </div>
                      <div class="text-center">
                        <div class="text-xs text-gray-500 dark:text-gray-400">
                          📈 Marge nette
                        </div>
                        <div
                          class="font-bold"
                          [class.text-green-600]="opp.isViable"
                          [class.text-amber-600]="!opp.isViable"
                        >
                          {{ opp.estimatedMargin.low || 0 }}–{{
                            opp.estimatedMargin.high || 0
                          }}€
                        </div>
                      </div>
                    </div>
                    <!-- Métriques détaillées -->
                    <div
                      class="grid grid-cols-2 gap-2 text-xs text-gray-600 dark:text-gray-400 mb-3"
                    >
                      <div class="flex items-center gap-1">
                        <span>�</span>
                        <span
                          ><strong>{{ opp.expectedAttendance }}</strong>
                          visiteurs attendus</span
                        >
                      </div>
                      <div class="flex items-center gap-1">
                        <span>🛒</span>
                        <span
                          >~<strong>{{ opp.expectedCustomers }}</strong> clients
                          potentiels</span
                        >
                      </div>
                      <div class="flex items-center gap-1">
                        <span>🎯</span>
                        <span
                          >Score <strong>{{ opp.score }}/100</strong></span
                        >
                      </div>
                      <div class="flex items-center gap-1">
                        <span>⏱️</span>
                        <span
                          >Charge <strong>{{ opp.capacityUsed }}%</strong> de ta
                          capacité</span
                        >
                      </div>
                    </div>
                    <!-- Actions -->
                    <div class="flex gap-2">
                      <button
                        (click)="onOpportunityFollowed(opp)"
                        class="flex-1 text-white text-sm font-medium py-2 px-4 rounded-lg transition-colors"
                        [class.bg-indigo-600]="opp.isViable"
                        [class.hover:bg-indigo-700]="opp.isViable"
                        [class.bg-gray-400]="!opp.isViable"
                        [class.hover:bg-gray-500]="!opp.isViable"
                      >
                        {{
                          opp.isViable
                            ? "➕ Suivre cette opportunité"
                            : "👁️ Surveiller"
                        }}
                      </button>
                      @if (opp.url) {
                        <a
                          [href]="opp.url"
                          target="_blank"
                          class="px-4 py-2 border border-gray-200 dark:border-slate-700 rounded-lg text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-slate-700 dark:bg-slate-900 transition-colors"
                        >
                          🔗 Détails
                        </a>
                      }
                    </div>
                  </div>
                }
              </div>
            </div>
          </section>
        }
      </ng-template>
      <!-- Aperçu des alertes (plans sans alertes automatiques, ex. Starter) -->
      @if (
        unifiedAlerts.length > 0 &&
        !planFeatureService.canAccess(FEATURES.AUTO_ALERTS)
      ) {
        <section
          class="bg-white dark:bg-slate-800 rounded-xl shadow-sm dark:shadow-slate-900/30 p-4 md:p-6"
        >
          <h2
            class="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2"
          >
            <span>⚠️</span> Alertes importantes
          </h2>
          <app-alert-preview [alerts]="unifiedAlerts" />
        </section>
      }
      <!-- Alertes importantes (météo + événements + calendrier) -->
      @if (
        unifiedAlerts.length > 0 &&
        planFeatureService.canAccess(FEATURES.AUTO_ALERTS)
      ) {
        <section
          class="bg-white dark:bg-slate-800 rounded-xl shadow-sm dark:shadow-slate-900/30 p-4 md:p-6"
        >
          <h2
            class="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2"
          >
            <span>⚠️</span> Alertes importantes
            <span
              class="px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300"
              >{{ unifiedAlerts.length }}</span
            >
          </h2>
          <div class="space-y-3">
            @for (alert of unifiedAlerts; track alert.date + alert.title) {
              <div
                class="border-l-4 p-4 rounded-r-lg"
                [ngClass]="alertService.getAlertClasses(alert)"
              >
                <div class="flex items-start gap-3">
                  <span class="text-2xl">{{ alert.icon }}</span>
                  <div class="flex-1 min-w-0">
                    <div class="flex items-center gap-2 flex-wrap">
                      <p
                        class="font-semibold"
                        [ngClass]="alertService.getAlertTitleClass(alert)"
                      >
                        {{ alert.title }}
                      </p>
                      <span
                        class="text-xs px-2 py-0.5 rounded-full font-bold"
                        [ngClass]="
                          alertService.getImpactBadgeClass(alert.impactPct)
                        "
                      >
                        {{ alert.impactPct > 0 ? "+" : ""
                        }}{{ alert.impactPct }}%
                      </span>
                      <span
                        class="text-xs px-2 py-0.5 rounded-full font-medium"
                        [ngClass]="
                          alertService.getConfidenceClass(alert.confidence)
                        "
                      >
                        🎯 {{ alert.confidence }}%
                      </span>
                    </div>
                    <button
                      (click)="alert.expanded = !alert.expanded"
                      class="text-xs mt-1 underline cursor-pointer"
                      [ngClass]="alertService.getAlertLinkClass(alert)"
                    >
                      {{ alert.expanded ? "Masquer" : "Voir" }} le détail
                    </button>
                    @if (alert.expanded) {
                      <p
                        class="text-sm mt-2 leading-relaxed"
                        [ngClass]="alertService.getAlertDetailClass(alert)"
                      >
                        {{ alert.detail }}
                      </p>
                    }
                  </div>
                  <div class="text-right flex-shrink-0">
                    <span
                      class="text-xs px-2 py-1 rounded block whitespace-nowrap"
                      [ngClass]="alertService.getAlertDateBadgeClass(alert)"
                    >
                      📍 {{ alert.dateLabel }}
                    </span>
                  </div>
                </div>
              </div>
            }
          </div>
        </section>
      }
      <!-- Alertes météo -->
      @if (weatherAlerts.length > 0) {
        <section
          class="bg-white dark:bg-slate-800 rounded-xl shadow-sm dark:shadow-slate-900/30 p-4 md:p-6"
        >
          <h2
            class="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2"
          >
            <span>🌦️</span> Alertes météo
          </h2>
          <div class="space-y-3">
            @for (alert of weatherAlerts; track alert.id) {
              <div
                class="p-4 rounded-lg border-2"
                [ngClass]="getAlertSeverityClasses(alert.severity)"
              >
                <div class="flex items-start gap-3">
                  <span class="text-2xl">{{ alert.icon }}</span>
                  <div class="flex-1">
                    <p class="font-bold text-gray-900 dark:text-white">
                      {{ alert.title }}
                    </p>
                    <p class="text-sm text-gray-700 dark:text-gray-300 mt-1">
                      {{ alert.message }}
                    </p>
                    <p class="text-xs text-gray-500 dark:text-gray-400 mt-2">
                      {{ alert.eventName }} • {{ alert.date }}
                    </p>
                  </div>
                </div>
              </div>
            }
          </div>
        </section>
      }
    </div>
  </div>
  <!-- EMPTY STATE: Onboarding amélioré -->
  @if (isEmptyState) {
    <div class="space-y-6">
      <!-- Hero Welcome -->
      <div
        class="bg-gradient-to-br from-purple-600 via-purple-700 to-indigo-800 rounded-2xl shadow-xl dark:shadow-slate-900/60 p-8 text-white relative overflow-hidden"
      >
        <div
          class="absolute top-0 right-0 w-64 h-64 bg-white dark:bg-slate-800/10 rounded-full -translate-y-1/2 translate-x-1/2"
        ></div>
        <div
          class="absolute bottom-0 left-0 w-48 h-48 bg-white dark:bg-slate-800/5 rounded-full translate-y-1/2 -translate-x-1/2"
        ></div>
        <div class="relative z-10">
          <div class="flex items-center gap-3 mb-4">
            <span class="text-5xl">🚀</span>
            <div>
              <h2 class="text-2xl md:text-3xl font-bold">
                Bienvenue {{ businessName || "sur votre dashboard" }} !
              </h2>
              <p class="text-purple-200 text-sm md:text-base">
                Votre assistant IA pour optimiser vos événements itinérants
              </p>
            </div>
          </div>
          <!-- Stats de démarrage -->
          <div class="grid grid-cols-3 gap-4 mt-6 max-w-lg">
            <div
              class="text-center p-3 bg-white dark:bg-slate-800/10 rounded-lg backdrop-blur-sm"
            >
              <p class="text-2xl font-bold">0</p>
              <p class="text-xs text-purple-200">Événements</p>
            </div>
            <div
              class="text-center p-3 bg-white dark:bg-slate-800/10 rounded-lg backdrop-blur-sm"
            >
              <p class="text-2xl font-bold">75%</p>
              <p class="text-xs text-purple-200">Précision IA</p>
            </div>
            <div
              class="text-center p-3 bg-white dark:bg-slate-800/10 rounded-lg backdrop-blur-sm"
            >
              <p class="text-2xl font-bold">∞</p>
              <p class="text-xs text-purple-200">Potentiel</p>
            </div>
          </div>
        </div>
      </div>
      <!-- Layout 2 colonnes pour Empty State -->
      <div class="grid lg:grid-cols-2 gap-6">
        <!-- Colonne gauche: Guide démarrage + CTA -->
        <div class="space-y-6">
          <!-- Quick Start Guide -->
          <section
            class="bg-white dark:bg-slate-800 rounded-xl shadow-sm dark:shadow-slate-900/30 p-6"
          >
            <h3
              class="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2"
            >
              <span>🎯</span> Démarrage rapide
            </h3>
            <div class="space-y-4">
              <div
                class="flex items-start gap-4 p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg border-2 border-purple-200 dark:border-purple-800"
              >
                <div
                  class="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-purple-600 text-white rounded-full font-bold"
                >
                  1
                </div>
                <div class="flex-1">
                  <p class="font-semibold text-gray-900 dark:text-white">
                    Créez votre premier événement
                  </p>
                  <p class="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Marché, foire, festival... l'IA adaptera ses
                    recommandations.
                  </p>
                  <button
                    (click)="createEvent()"
                    class="mt-3 px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 transition inline-flex items-center gap-2"
                  >
                    <span>➕</span> Créer un événement
                  </button>
                </div>
              </div>
              <div
                class="flex items-start gap-4 p-4 bg-gray-50 dark:bg-slate-900 rounded-lg border border-gray-200 dark:border-slate-700 opacity-60"
              >
                <div
                  class="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-gray-400 text-white rounded-full font-bold"
                >
                  2
                </div>
                <div class="flex-1">
                  <p class="font-semibold text-gray-900 dark:text-white">
                    Recevez vos recommandations
                  </p>
                  <p class="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    L'IA calcule les quantités optimales selon la météo, le lieu
                    et le type.
                  </p>
                </div>
              </div>
              <div
                class="flex items-start gap-4 p-4 bg-gray-50 dark:bg-slate-900 rounded-lg border border-gray-200 dark:border-slate-700 opacity-60"
              >
                <div
                  class="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-gray-400 text-white rounded-full font-bold"
                >
                  3
                </div>
                <div class="flex-1">
                  <p class="font-semibold text-gray-900 dark:text-white">
                    Saisissez vos ventes réelles
                  </p>
                  <p class="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    L'IA apprend et améliore ses prédictions à chaque événement.
                  </p>
                </div>
              </div>
            </div>
          </section>
          <!-- Opportunités en évidence -->
          @if (opportunities.length > 0) {
            <section
              class="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl shadow-sm dark:shadow-slate-900/30 p-6 border-2 border-blue-200 dark:border-blue-800"
            >
              <h3
                class="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2"
              >
                <span>💡</span> Opportunités près de vous
                <span
                  class="ml-auto text-xs px-2 py-1 bg-blue-600 text-white rounded-full"
                >
                  {{ opportunities.length }} trouvées
                </span>
              </h3>
              <div class="space-y-3">
                @for (
                  opp of opportunities.slice(0, 3);
                  track opp.id;
                  let i = $index
                ) {
                  <div
                    class="flex items-center justify-between p-4 bg-white dark:bg-slate-800 rounded-lg border border-blue-200 dark:border-blue-800 hover:border-blue-400 dark:border-blue-700 hover:shadow-md dark:shadow-slate-900/50 transition cursor-pointer"
                    (click)="enrollOpportunity(opp)"
                  >
                    <div class="flex items-center gap-3">
                      <span class="text-2xl">{{
                        i === 0 ? "🥇" : i === 1 ? "🥈" : "🥉"
                      }}</span>
                      <div>
                        <p class="font-semibold text-gray-900 dark:text-white">
                          {{ opp.name }}
                        </p>
                        <p class="text-xs text-gray-500 dark:text-gray-400">
                          {{ opp.location }}
                        </p>
                      </div>
                    </div>
                    <div class="text-right">
                      <p
                        class="text-sm font-bold text-blue-600 dark:text-blue-400"
                      >
                        {{ opp.score }}/100
                      </p>
                      <p class="text-xs text-gray-500 dark:text-gray-400">
                        Score
                      </p>
                    </div>
                  </div>
                }
              </div>
              <button
                (click)="viewOpportunities()"
                class="mt-4 w-full py-2 text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:text-blue-300 hover:underline"
              >
                Voir toutes les opportunités →
              </button>
            </section>
          }
          <!-- Message si pas d'opportunités -->
          @if (opportunities.length === 0) {
            <section
              class="bg-gray-50 dark:bg-slate-900 rounded-xl shadow-sm dark:shadow-slate-900/30 p-6 border border-gray-200 dark:border-slate-700 text-center"
            >
              <span class="text-4xl mb-3 block">🔍</span>
              <p class="font-semibold text-gray-900 dark:text-white">
                Recherche d'opportunités...
              </p>
              <p class="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Vérifiez votre zone d'activité sur la carte pour découvrir des
                événements proches.
              </p>
            </section>
          }
        </div>
        <!-- Colonne droite: Carte + Insights -->
        <div class="space-y-6">
          <!-- Carte quand position configurée -->
          @if (merchantLocation) {
            <app-map-widget
              [merchantLocation]="merchantLocation"
              [events]="mapEvents"
              [opportunities]="filteredOpportunities"
              [initialFollowedIds]="followedOpportunityIds"
              [oppCountOverride]="filteredOpportunities.length"
              (radiusChanged)="onRadiusChanged($event)"
              (opportunityFollowed)="onOpportunityFollowed($event)"
            >
            </app-map-widget>
          }
          <!-- 💡 Opportunités recommandées -->
          <ng-container *ngTemplateOutlet="oppSection"></ng-container>
          <!-- Message si position non configurée -->
          @if (!merchantLocation) {
            <section
              class="bg-white dark:bg-slate-800 rounded-xl shadow-sm dark:shadow-slate-900/30 p-6"
            >
              <h3
                class="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2"
              >
                <span>🗺️</span> Votre zone d'activité
              </h3>
              <div
                class="aspect-video bg-gray-100 dark:bg-slate-800 rounded-lg flex items-center justify-center"
              >
                <div class="text-center p-6">
                  <span class="text-4xl mb-3 block">📍</span>
                  <p class="font-semibold text-gray-900 dark:text-white">
                    Position non configurée
                  </p>
                  <p class="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Allez dans Paramètres pour définir votre emplacement.
                  </p>
                  <button
                    routerLink="/settings"
                    class="mt-3 px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 transition"
                  >
                    Configurer ma position
                  </button>
                </div>
              </div>
            </section>
          }
          <!-- Ce que l'IA fait pour vous -->
          <section
            class="bg-white dark:bg-slate-800 rounded-xl shadow-sm dark:shadow-slate-900/30 p-6"
          >
            <h3
              class="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2"
            >
              <span>🤖</span> Ce que l'IA fait pour vous
            </h3>
            <div class="grid grid-cols-2 gap-3">
              <div
                class="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg text-center"
              >
                <span class="text-2xl mb-2 block">📦</span>
                <p class="text-sm font-semibold text-gray-900 dark:text-white">
                  Stock optimal
                </p>
                <p class="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  Ni trop, ni trop peu
                </p>
              </div>
              <div
                class="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-center"
              >
                <span class="text-2xl mb-2 block">🌦️</span>
                <p class="text-sm font-semibold text-gray-900 dark:text-white">
                  Météo intégrée
                </p>
                <p class="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  Prévisions adaptées
                </p>
              </div>
              <div
                class="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg text-center"
              >
                <span class="text-2xl mb-2 block">💰</span>
                <p class="text-sm font-semibold text-gray-900 dark:text-white">
                  Moins de pertes
                </p>
                <p class="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  Économies garanties
                </p>
              </div>
              <div
                class="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg text-center"
              >
                <span class="text-2xl mb-2 block">📈</span>
                <p class="text-sm font-semibold text-gray-900 dark:text-white">
                  Auto-apprentissage
                </p>
                <p class="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  S'améliore avec vous
                </p>
              </div>
            </div>
          </section>
          <!-- Conseils pro -->
          <section
            class="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl shadow-sm dark:shadow-slate-900/30 p-6 border border-amber-200 dark:border-amber-800"
          >
            <h3
              class="text-lg font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2"
            >
              <span>💡</span> Conseil du jour
            </h3>
            <p class="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
              <strong>Astuce :</strong> Pour des recommandations optimales dès
              le départ, commencez par un événement que vous connaissez bien.
              L'IA apprendra plus vite de vos retours sur un terrain familier !
            </p>
          </section>
        </div>
      </div>
      <!-- CTA Final -->
      <div class="text-center py-8">
        <button
          (click)="createEvent()"
          class="px-10 py-5 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-bold text-lg rounded-xl hover:from-purple-700 hover:to-indigo-700 transition shadow-xl dark:shadow-slate-900/60 hover:shadow-2xl transform hover:-translate-y-1"
        >
          🎉 Créer mon premier événement
        </button>
        <p class="text-sm text-gray-500 dark:text-gray-400 mt-3">
          C'est gratuit et ça prend moins de 2 minutes
        </p>
      </div>
    </div>
  }
</div>

<!-- ─── Modal confirmation suppression ─────────────────────────── -->
@if (deleteConfirmEvent) {
  <div
    class="fixed inset-0 flex items-center justify-center"
    style="z-index: 10000"
    (click)="cancelDelete()"
  >
    <!-- Backdrop -->
    <div class="absolute inset-0 bg-black/40 backdrop-blur-sm"></div>
    <!-- Panel -->
    <div
      class="relative bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden"
      (click)="$event.stopPropagation()"
    >
      <!-- Header rouge -->
      <div
        class="bg-red-50 dark:bg-red-900/20 px-6 pt-6 pb-4 flex items-start gap-4"
      >
        <div
          class="flex-shrink-0 w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center text-2xl"
        >
          🗑️
        </div>
        <div>
          <h3 class="text-lg font-semibold text-gray-900 dark:text-white">
            Supprimer l'événement
          </h3>
          <p class="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            Cette action est irréversible.
          </p>
        </div>
      </div>
      <!-- Body -->
      <div class="px-6 py-4">
        <p class="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
          Vous êtes sur le point de supprimer
          <span class="font-semibold text-gray-900 dark:text-white"
            >« {{ deleteConfirmEvent.name }} »</span
          >.
        </p>
        <p class="text-gray-500 dark:text-gray-400 text-xs mt-2">
          📍 {{ formatLocation(deleteConfirmEvent.location) }} &nbsp;•&nbsp;
          {{ formatEventDate(deleteConfirmEvent.eventDate) }}
        </p>
        @if (deleteConfirmEvent.externalId) {
          <div
            class="mt-3 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-xs text-amber-700 dark:text-amber-300"
          >
            💡 L'opportunité associée redeviendra disponible dans les
            recommandations.
          </div>
        }
      </div>
      <!-- Actions -->
      <div class="px-6 pb-6 flex gap-3 justify-end">
        <button
          (click)="cancelDelete()"
          class="px-5 py-2.5 rounded-xl border border-gray-200 dark:border-slate-700 text-gray-600 dark:text-gray-400 text-sm font-medium hover:bg-gray-50 dark:hover:bg-slate-700 dark:bg-slate-900 transition"
        >
          Annuler
        </button>
        <button
          (click)="confirmDelete()"
          class="px-5 py-2.5 rounded-xl bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition shadow-sm dark:shadow-slate-900/30"
        >
          Supprimer
        </button>
      </div>
    </div>
  </div>
}

<!-- MODAL DÉTAIL JOUR TIMELINE -->
@if (showTimelineModal && selectedTimelineDay) {
  <div
    class="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
    (click)="closeTimelineModal()"
  >
    <div
      class="bg-white dark:bg-slate-800 rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-lg max-h-[90vh] overflow-y-auto"
      (click)="$event.stopPropagation()"
    >
      <!-- Header -->
      <div
        class="flex items-center justify-between p-5 border-b border-gray-100 dark:border-slate-700 sticky top-0 bg-white dark:bg-slate-800 rounded-t-2xl z-10"
      >
        <div>
          <h2
            class="text-lg font-bold text-gray-900 dark:text-white capitalize"
          >
            {{ selectedTimelineDay.date | date: "EEEE d MMMM" }}
          </h2>
          @if (selectedTimelineDay.weather) {
            <div class="flex items-center gap-3 mt-1">
              <span class="text-3xl">{{
                selectedTimelineDay.weather.icon
              }}</span>
              <div>
                @if (selectedTimelineDay.weather.condition) {
                  <p
                    class="text-sm font-medium text-gray-700 dark:text-slate-200"
                  >
                    {{ selectedTimelineDay.weather.condition }}
                  </p>
                }
                <p class="text-sm text-gray-500 dark:text-gray-400">
                  @if (
                    selectedTimelineDay.weather.temperatureMin !== undefined
                  ) {
                    🌅 Matin {{ selectedTimelineDay.weather.temperatureMin }}° ·
                    ☀️ Après-midi
                    {{ selectedTimelineDay.weather.temperatureMax }}°
                  } @else {
                    {{ selectedTimelineDay.weather.temperature }}°C
                  }
                </p>
              </div>
            </div>
          } @else {
            <span
              class="inline-flex items-center gap-1 text-xs text-gray-400 dark:text-slate-500 bg-gray-100 dark:bg-slate-700 rounded-full px-2 py-0.5"
            >
              💫 Météo indisponible (&gt;7j)
            </span>
          }
        </div>
        <button
          (click)="closeTimelineModal()"
          class="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 dark:text-gray-300 text-2xl leading-none w-9 h-9 flex items-center justify-center rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 dark:bg-slate-800 transition"
        >
          ✕
        </button>
      </div>

      <!-- Body -->
      <div class="p-5 space-y-5">
        <!-- Périodes spéciales actives -->
        @if (selectedTimelineDay.specialPeriods?.length) {
          @for (sp of selectedTimelineDay.specialPeriods!; track sp.label) {
            <div
              class="flex items-center gap-3 p-3 rounded-xl border"
              [style.border-color]="sp.color || '#a78bfa'"
              [style.background-color]="(sp.color || '#a78bfa') + '18'"
            >
              <span class="text-xl">{{ PERIOD_TYPE_META[sp.type]?.icon }}</span>
              <div class="flex-1 min-w-0">
                <p
                  class="font-medium text-sm"
                  [style.color]="sp.color || '#7c3aed'"
                >
                  {{ sp.label }}
                </p>
                <p class="text-xs text-gray-500 dark:text-gray-400">
                  Période spéciale active
                </p>
              </div>
              <button
                (click)="openPeriodForm(merchantPeriods().find(p => p.label === sp.label) ?? null); closeTimelineModal()"
                class="ml-auto text-xs text-purple-600 dark:text-purple-400 hover:underline whitespace-nowrap"
              >
                Modifier
              </button>
            </div>
          }
        }
        @if (
          selectedTimelineDay.events.length === 0 &&
          selectedTimelineDay.opportunities.length === 0
        ) {
          <div class="text-center py-10 text-gray-500 dark:text-gray-400">
            <p class="text-4xl mb-3">📭</p>
            <p class="text-sm mb-1">Rien de prévu ce jour-là.</p>
            @if (planFeatureService.canAccess(FEATURES.ACTIVITY_ZONE)) {
              <button
                (click)="viewOpportunities(); closeTimelineModal()"
                class="mt-2 text-sm text-purple-600 hover:underline font-medium"
              >
                Explorer les opportunités →
              </button>
            } @else {
              <div
                class="mt-2 inline-flex items-center gap-1.5 text-xs text-gray-400 dark:text-slate-500 bg-gray-100 dark:bg-slate-700 rounded-full px-3 py-1"
              >
                <span>🔒</span>
                <span>Opportunités — Plan Pro</span>
                <a
                  routerLink="/subscription"
                  (click)="closeTimelineModal()"
                  class="text-purple-500 hover:underline font-medium ml-1"
                  >Passer Pro</a
                >
              </div>
            }
          </div>
        }

        <!-- Événements du jour -->
        @if (selectedTimelineDay.events.length > 0) {
          <div>
            <h3
              class="text-xs font-semibold text-purple-700 dark:text-purple-300 uppercase tracking-wide mb-3"
            >
              Événements ({{ selectedTimelineDay.events.length }})
            </h3>
            <div class="space-y-2">
              @for (event of selectedTimelineDay.events; track event.id) {
                <div
                  class="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800 hover:border-purple-400 transition"
                >
                  <div class="flex items-start justify-between mb-1">
                    <div class="flex items-start gap-3">
                      <span class="text-xl">{{
                        getEventIcon(event.eventType)
                      }}</span>
                      <div>
                        <p class="font-medium text-gray-900 dark:text-white">
                          {{ event.name }}
                        </p>
                        <p class="text-xs text-gray-500 dark:text-gray-400">
                          {{ formatLocation(event.location) }} •
                          {{ formatEventDate(event.eventDate) }}
                        </p>
                        <p class="text-xs text-gray-500 dark:text-gray-400">
                          {{ event.startTime
                          }}{{ event.endTime ? " - " + event.endTime : "" }}
                        </p>
                      </div>
                    </div>
                    <span class="text-xs font-medium text-purple-600">
                      {{ getDaysUntilLabel(event.eventDate) }}
                    </span>
                  </div>
                  @if (event.estimatedMargin) {
                    <div
                      class="flex items-center gap-4 mt-2 pt-2 border-t border-purple-100 text-xs"
                    >
                      <span class="text-gray-600 dark:text-gray-400">
                        📈 Marge:
                        <strong class="text-green-600 dark:text-green-400">
                          {{
                            ((event.estimatedMargin.low || 0) +
                              (event.estimatedMargin.high || 0)) /
                              2 | number: "1.0-0"
                          }}€
                        </strong>
                      </span>
                      <span class="text-gray-600 dark:text-gray-400">
                        👥 Affluence:
                        <strong>{{
                          event.estimatedAttendance ||
                            event.expectedAttendance ||
                            "?"
                        }}</strong>
                      </span>
                    </div>
                  }
                  <div class="flex justify-end items-center gap-2 mt-2">
                    <button
                      (click)="viewEvent(event.id); closeTimelineModal()"
                      class="text-xs font-medium text-purple-600 hover:text-purple-800 dark:text-purple-300 border border-purple-300 hover:border-purple-500 rounded-lg px-3 py-1 transition bg-white dark:bg-slate-800"
                    >
                      📋 Détails
                    </button>
                    @if (findEventById(event.id)) {
                      <button
                        (click)="deleteEvent(findEventById(event.id), $event)"
                        class="text-red-400 hover:text-red-600 dark:text-red-400 transition text-lg leading-none"
                        title="Supprimer"
                      >
                        🗑️
                      </button>
                    }
                  </div>
                </div>
              }
            </div>
          </div>
        }

        <!-- Opportunités du jour -->
        @if (selectedTimelineDay.opportunities.length > 0) {
          <div>
            <h3
              class="text-xs font-semibold text-blue-700 dark:text-blue-300 uppercase tracking-wide mb-3"
            >
              Opportunités ({{ selectedTimelineDay.opportunities.length }})
            </h3>
            <div class="space-y-3">
              @for (opp of selectedTimelineDay.opportunities; track opp.id) {
                <div
                  class="border rounded-lg p-4 transition-colors"
                  [ngClass]="{
                    'border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 hover:border-green-400 dark:border-green-700':
                      opp.profitabilityLevel === 'high',
                    'border-yellow-200 dark:border-yellow-800 bg-yellow-50 dark:bg-yellow-900/20 hover:border-yellow-400 dark:border-yellow-700':
                      opp.profitabilityLevel === 'medium',
                    'border-orange-200 dark:border-orange-800 bg-orange-50 dark:bg-orange-900/20 hover:border-orange-400':
                      opp.profitabilityLevel === 'low',
                  }"
                >
                  <div class="flex items-start justify-between mb-2">
                    <div>
                      <div class="flex items-center gap-2">
                        <span
                          class="w-3 h-3 rounded-full flex-shrink-0"
                          [ngClass]="{
                            'bg-green-500': opp.profitabilityLevel === 'high',
                            'bg-yellow-400':
                              opp.profitabilityLevel === 'medium',
                            'bg-orange-500': opp.profitabilityLevel === 'low',
                          }"
                        ></span>
                        <h3 class="font-semibold text-gray-900 dark:text-white">
                          {{ opp.name }}
                        </h3>
                      </div>
                      <p class="text-sm text-gray-500 dark:text-gray-400 ml-5">
                        📍 {{ opp.location }} · {{ opp.distance }}
                      </p>
                    </div>
                    <span
                      class="px-2 py-1 rounded-full text-xs font-medium"
                      [class.bg-green-100]="opp.activityMatch >= 80"
                      [class.text-green-700]="opp.activityMatch >= 80"
                      [class.bg-blue-100]="
                        opp.activityMatch >= 60 && opp.activityMatch < 80
                      "
                      [class.text-blue-700]="
                        opp.activityMatch >= 60 && opp.activityMatch < 80
                      "
                      [class.bg-gray-100]="opp.activityMatch < 60"
                      [class.text-gray-600]="opp.activityMatch < 60"
                      >{{ opp.activityMatchLabel }}</span
                    >
                  </div>
                  <div
                    class="flex flex-wrap gap-3 text-sm text-gray-600 dark:text-gray-400 mb-3"
                  >
                    <span>📅 {{ opp.dateFormatted }}</span>
                    @if (opp.timeRange) {
                      <span>🕐 {{ opp.timeRange }}</span>
                    }
                    <span>{{ opp.eventTypeLabel }}</span>
                  </div>
                  @if (!opp.isViable) {
                    <div
                      class="mb-3 px-3 py-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-sm"
                    >
                      <span
                        class="font-medium text-amber-700 dark:text-amber-300"
                        >⚠️ Rentabilité incertaine</span
                      >
                      <span class="text-amber-600">
                        — CA minimum conseillé : {{ opp.minViableCA }}€</span
                      >
                    </div>
                  }
                  <div
                    class="grid grid-cols-2 gap-2 bg-gray-50 dark:bg-slate-900 rounded-lg p-3 mb-3"
                  >
                    <div class="text-center">
                      <div class="text-xs text-gray-500 dark:text-gray-400">
                        💰 CA estimé
                      </div>
                      <div
                        class="font-bold"
                        [class.text-green-600]="opp.isViable"
                        [class.text-amber-600]="!opp.isViable"
                      >
                        {{ opp.estimatedRevenue.low }}–{{
                          opp.estimatedRevenue.high
                        }}€
                      </div>
                    </div>
                    <div class="text-center">
                      <div class="text-xs text-gray-500 dark:text-gray-400">
                        📈 Marge nette
                      </div>
                      <div
                        class="font-bold"
                        [class.text-green-600]="opp.isViable"
                        [class.text-amber-600]="!opp.isViable"
                      >
                        {{ opp.estimatedMargin.low || 0 }}–{{
                          opp.estimatedMargin.high || 0
                        }}€
                      </div>
                    </div>
                  </div>
                  <div class="flex gap-2">
                    <button
                      (click)="onOpportunityFollowed(opp); closeTimelineModal()"
                      class="flex-1 text-white text-sm font-medium py-2 px-4 rounded-lg transition-colors"
                      [class.bg-indigo-600]="opp.isViable"
                      [class.hover:bg-indigo-700]="opp.isViable"
                      [class.bg-gray-400]="!opp.isViable"
                      [class.hover:bg-gray-500]="!opp.isViable"
                    >
                      {{
                        opp.isViable
                          ? "➕ Suivre cette opportunité"
                          : "👁️ Surveiller"
                      }}
                    </button>
                    @if (opp.url) {
                      <a
                        [href]="opp.url"
                        target="_blank"
                        class="px-4 py-2 border border-gray-200 dark:border-slate-700 rounded-lg text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-slate-700 dark:bg-slate-900 transition-colors"
                        >🔗 Détails</a
                      >
                    }
                  </div>
                </div>
              }
            </div>
          </div>
        }
      </div>
    </div>
  </div>
}

<!-- Conflict warning modal -->
@if (conflictWarning.length > 0) {
  <app-event-conflict-warning
    [conflicts]="conflictWarning"
    (confirm)="onConflictConfirm()"
    (cancel)="onConflictCancel()"
  ></app-event-conflict-warning>
}
